import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Autocomplete, TextField, Checkbox, Paper } from '@mui/material';
import { debounce } from 'lodash';
import { CLIENT_BASIC_SEARCH_URL, clientSearchKeys, toSearchBody } from 'utils/clientSearch';
import { inputsStyle } from './styles';
import { InputMinLoader } from './inputMiniLoader';
import { TextRow } from '../table';
import { useNavigate, useLocation } from 'react-router-dom';
import { ErrMessage } from '../messages';
import { Colors, ErrorText, Images, useGlobalStyles, User } from 'utils';
import { SelectEnums } from './constants';
import { MinLoader } from '../loader';

// Defined at module scope on purpose: an inline `PaperComponent={(...) => ...}` is a new component
// type on every render, so MUI unmounts and remounts the dropdown (and its listbox) after each pick,
// throwing the list back to the top. Per-render values come in through `slotProps.paper`.
const RefreshPaper = ({ children, loading, onRefresh, ...paperProps }) => (
    <Paper {...paperProps}>
        {children}
        <div onMouseDown={(e) => e.preventDefault()}>
            <div className="refresh-btn-wrapper">
                <button onClick={onRefresh} className="refresh-btn">
                    <div className="refresh-btn-image-box">
                        {loading ? (
                            <MinLoader height={16} width={17} color={'#0C66E4'} margin={'0'} position={'relative'} />
                        ) : (
                            <img src={Images.refreshSelect} alt="refresh" />
                        )}
                    </div>
                    <p>Refresh</p>
                </button>
            </div>
        </div>
    </Paper>
);

export const SelectAutocompletePaginationMultiple = ({
    title,
    name,
    handleSelect,
    style,
    disabled,
    error,
    placeholder,
    renderValue,
    uiType,
    outLabel,
    outStyle,
    type,
    defaultStatuses,
    noError,
    typeError,
    fillCurrent,
    sendParams,
    defaultList,
    defaultValue,
    autoSelect,
    inputStyles={}
}) => {
    const classes = inputsStyle();
    const [value, setValue] = useState([]);
    const [options, setOptions] = useState([]);
    const [page, setPage] = useState(1);
    const [open, setOpen] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const ITEMS_PER_PAGE = 50;
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const refreshClicked = useRef(false);
    const { userInfo } = User();
    const globalStyles = useGlobalStyles();
    // `fetchOptions` is a debounced callback created once per `type`, so it must read the CURRENT
    // `defaultStatuses` through a ref — the prop it closed over on mount would otherwise be the one
    // sent forever (the map's Status filter changes it after mount). The key is a string so callers
    // passing a fresh array literal every render don't refetch every render.
    const defaultStatusesRef = useRef(defaultStatuses);
    defaultStatusesRef.current = defaultStatuses;
    const defaultStatusesKey = Array.isArray(defaultStatuses) ? defaultStatuses.join(',') : '';
    const statusesMountedRef = useRef(false);

    useEffect(() => {
        if (fillCurrent && value.length === 0 && options?.length) {
            const currentStaff = options.find((i) => i.id === userInfo?.id);
            if (currentStaff) {
                handleChange(null, [currentStaff]);
            }
        }
        if (autoSelect && options?.length === 1 && value.length === 0) {
            handleChange(null, [options[0]]);
        }
    }, [options]);

    const fetchOptions = useCallback(
        debounce(async (page, searchTerm, statuses, info) => {
            try {
                setLoading(true);
                const apiUrl =
                    type === 'staff'
                        ? '/staff/basic/info'
                        : type === 'client'
                          ? CLIENT_BASIC_SEARCH_URL
                          : type === 'fullStaffList'
                            ? '/staff'
                            : '';

                const params = {
                    skip: (page - 1) * ITEMS_PER_PAGE,
                    limit: ITEMS_PER_PAGE,
                    statuses: statuses,
                    searchValue: searchTerm || undefined,
                    searchKey: searchTerm ? (type === 'staff' ? 'NAME' : clientSearchKeys.ANY) : undefined,
                    sortType: type === 'staff' ? 'fullName' : 'code',
                    orderType: 'ASC',
                };

                if (!info || !info?.showActive || info?.showActive === false) {
                    params.statuses = defaultStatusesRef.current;
                } else if (info?.showActive === true) {
                    params.statuses = [];
                }

                // params.statuses = info?.showActive ? [] : defaultStatuses;

                // Client search text is PHI — it goes in a POST body, never the URL.
                const response =
                    type === 'client'
                        ? await axios.post(CLIENT_BASIC_SEARCH_URL, toSearchBody(params), { auth: true })
                        : await axios.get(apiUrl, {
                              params,
                              auth: true,
                          });

                const data = response?.data || [];
                setOptions((prev) => (page === 1 ? [...data] : [...prev, ...data]));
                setHasMore(data.length >= ITEMS_PER_PAGE);
            } catch (error) {
                if (error?.status === 403 && defaultList?.length) {
                    setOptions(defaultList);
                }
            } finally {
                setLoading(false);
            }
        }, 300),
        [type]
    );

    useEffect(() => {
        fetchOptions(page, searchTerm, defaultStatuses, info);
    }, [page, searchTerm, fetchOptions]);

    useEffect(() => {
        if (info?.showActive === undefined || info?.showActive === false) {
            fetchOptions(page, searchTerm, defaultStatuses, info, sendParams);
        } else if (info?.showActive === true) {
            fetchOptions(page, searchTerm, [], info, sendParams);
        }
    }, [info?.showActive, sendParams]);

    // A new status set invalidates every loaded page: start over from page 1 with the new statuses.
    useEffect(() => {
        if (!statusesMountedRef.current) {
            statusesMountedRef.current = true;
            return;
        }
        setPage(1);
        setOptions([]);
        setHasMore(true);
        fetchOptions(1, searchTerm, defaultStatusesRef.current, info, sendParams);
    }, [defaultStatusesKey]);

    const handleScroll = (event) => {
        const listboxNode = event.currentTarget;
        if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50) {
            if (hasMore && !loading) {
                setPage((prev) => prev + 1);
            }
        }
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
        handleSelect({
            target: {
                value: newValue?.map((v) => v.id) || [],
                full: newValue,
                name,
            },
        });
    };

    const handleInputChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(1);
        setOptions([]);
        setHasMore(true);
    };

    // `options` holds only the loaded pages (first 50 by code once the search clears), so a pick found
    // by search is often not in it. Keep the picked object we already have instead of dropping it.
    useEffect(() => {
        if (defaultValue && options?.length && !searchTerm) {
            setValue((prev) =>
                defaultValue
                    .map((dv) => {
                        const id = dv?.id ? dv.id : dv;
                        return options.find((opt) => opt.id === id) || prev.find((v) => v?.id === id) || (dv?.id ? dv : null);
                    })
                    .filter(Boolean)
            );
        }
    }, [defaultValue, options, searchTerm]);

    return (
        <div className={style || ''}>
            {outLabel && (
                <p id='labelTitle' className={classes.labelTitle} style={{ ...outStyle }}>
                    {outLabel}
                </p>
            )}
            <Autocomplete
                multiple
                className={
                    // uiType === 'tableFilter' ? classes.noChipLoads :
                    uiType === 'tableFilter'
                        ? classes.noChipSelectMultiple
                        : error === name
                          ? `autocomplete-main-wrapper-error`
                          : // globalStyles.inputTextFieldError :

                            `autocomplete-main-wrapper`
                }
                style={{ ...inputStyles }}
                disableCloseOnSelect
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => {
                    setOpen(false);
                    setSearchTerm('');
                    if (!options.length) fetchOptions(1, '', defaultStatuses, info, sendParams);
                }}
                onChange={(event, newValue) => {
                    handleChange(event, newValue);
                }}
                value={value}
                options={options}
                getOptionLabel={(option) => renderValue(option) || ''}
                isOptionEqualToValue={(option, value) => {
                    const optionId = option?.id || option?._id;
                    const valueId = value?.id || value?._id;
                    return optionId === valueId;
                }}
                filterOptions={(x) => x}
                ListboxProps={{ onScroll: handleScroll, style: { paddingBottom: '42px' } }}
                loading={loading}
                disabled={disabled}
                slots={{ paper: RefreshPaper }}
                slotProps={{
                    paper: {
                        loading,
                        onRefresh: () => fetchOptions(page, searchTerm, defaultStatuses, info, sendParams),
                    },
                }}
                renderOption={(props, option, { selected }) => {
                    const { key, ...restProps } = props;
                    return (
                        <li key={key} {...restProps}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox style={{ padding: '2px 8px 2px 0', color: Colors.ThemeBlue }} checked={selected} />
                                <TextRow name={renderValue(option)} textWidth={7} />
                            </div>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        slotProps={{
                            input: loading
                                ? {
                                      ...params.InputProps,
                                      endAdornment: <InputMinLoader />,
                                  }
                                : { ...params.InputProps },
                        }}
                        onChange={handleInputChange}
                        variant="outlined"
                        label={title}
                        placeholder={placeholder}
                        error={!!error?.message}
                    />
                )}
            />
            {!noError && <ErrMessage text={error === name ? typeError || ErrorText.field : ''} style={{ position: 'relative' }} />}
        </div>
    );
};
